import {
  JOB_DEPARTMENT_LABELS,
  JOB_EMPLOYMENT_TYPE_LABELS,
  type JobPosting,
} from '@resolute/schema';
import { Badge, Button, Card, Modal } from '@resolute/ui';
import { Briefcase, MapPin } from 'lucide-react';
import { useState } from 'react';


function experienceLabel(job: JobPosting): string {
  if (job.experienceMin === 0 && job.experienceMax === 0) return 'Freshers welcome';
  if (job.experienceMin === job.experienceMax) return `${job.experienceMin} years`;
  return `${job.experienceMin}-${job.experienceMax} years`;
}

export function JobCard({ job, onApply }: { job: JobPosting; onApply: (jobId: string) => void }) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <Card interactive className="flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{JOB_DEPARTMENT_LABELS[job.department]}</Badge>
        <Badge variant="outline">{JOB_EMPLOYMENT_TYPE_LABELS[job.employmentType]}</Badge>
      </div>

      <div>
        <h3 className="text-lg font-semibold tracking-tight text-foreground">{job.title}</h3>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
            {job.location}
          </span>
          <span className="flex items-center gap-1.5">
            <Briefcase className="size-3.5 shrink-0" aria-hidden="true" />
            {experienceLabel(job)}
          </span>
        </div>
      </div>

      <p className="line-clamp-2 text-sm text-muted-foreground">{job.description}</p>

      <div className="mt-auto flex flex-wrap gap-3 pt-2">
        <Button variant="outline" onClick={() => setDetailsOpen(true)}>
          View details
        </Button>
        <Button onClick={() => onApply(job.id)}>Apply now</Button>
      </div>

      <Modal
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        title={job.title}
        description={`${JOB_DEPARTMENT_LABELS[job.department]} · ${JOB_EMPLOYMENT_TYPE_LABELS[job.employmentType]} · ${job.location}`}
        className="max-w-2xl"
        footer={
          <Button
            onClick={() => {
              setDetailsOpen(false);
              onApply(job.id);
            }}
          >
            Apply for this role
          </Button>
        }
      >
        <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1 text-sm">
          <p className="text-muted-foreground">{job.description}</p>

          {job.responsibilities.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground">Responsibilities</h4>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                {job.responsibilities.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {job.requirements.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground">Requirements</h4>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                {job.requirements.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    </Card>
  );
}
